import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import './styles/glass-effects.css';
import './styles/theme-overrides.css';
import { logger } from '@/shared/utils';
import { ENV } from '@/config/environment';

// Helper function to check if error is from browser extension
const isBrowserExtensionError = (error: Error | string): boolean => {
  const errorMessage = typeof error === 'string' ? error : error.message || '';
  const errorString = errorMessage.toLowerCase();
  
  // Common browser extension error patterns
  const extensionErrorPatterns = [
    'message channel closed',
    'listener indicated an asynchronous response',
    'extension context invalidated',
    'receiving end does not exist',
    'message port closed',
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
  ];
  
  return extensionErrorPatterns.some(pattern => errorString.includes(pattern));
};

// Setup error handling for uncaught errors
window.addEventListener('error', (event) => {
  // Filter out browser extension errors
  if (isBrowserExtensionError(event.error || event.message)) {
    // Silently ignore browser extension errors
    return;
  }
  
  logger.error('Uncaught error', 'window.onerror', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
    appName: ENV.app.name,
    version: ENV.app.version,
  });
});

// Setup promise rejection handling
window.addEventListener('unhandledrejection', (event) => {
  // Filter out browser extension errors
  const reason = event.reason;
  if (
    (reason instanceof Error && isBrowserExtensionError(reason)) ||
    (typeof reason === 'string' && isBrowserExtensionError(reason))
  ) {
    // Silently ignore browser extension errors
    event.preventDefault(); // Prevent default console error
    return;
  }
  
  logger.error('Unhandled promise rejection', 'window.onunhandledrejection', {
    reason: event.reason,
    appName: ENV.app.name,
    version: ENV.app.version,
  });
});

// Log startup with environment information
logger.info('Application starting', 'APP', {
  appName: ENV.app.name,
  version: ENV.app.version,
  environment: ENV.app.environment,
  projectId: ENV.project.id,
  isDevelopment: ENV.app.isDevelopment,
});

// The static default meta/OG/Twitter tags in index.html exist for non-JS
// crawlers. Since JS is now running, react-helmet-async will inject the
// real per-page tags - remove the static defaults first so the two don't
// coexist (Helmet doesn't know about or clean up tags it didn't insert).
document.querySelectorAll('[data-default-seo]').forEach((el) => el.remove());

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);